'use client';

import { useCallback, useEffect, useState } from 'react';
import { useSession } from 'next-auth/react';

import { buildDirectBackendUrl, createDirectBackendHeaders } from '@/lib/direct-backend-api';
import type { ParagraphCommentCountsResponse } from '@/types/comment';

async function requestParagraphCommentCounts(
  postId: number,
  chapterId: string,
  accessToken: string | undefined,
  signal?: AbortSignal,
): Promise<ParagraphCommentCountsResponse> {
  const params = new URLSearchParams({
    postId: String(postId),
    chapterId,
  });

  const response = await fetch(
    buildDirectBackendUrl(`/api/comments/paragraph-counts?${params.toString()}`),
    {
      headers: createDirectBackendHeaders({
        accessToken,
      }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch paragraph comment counts');
  }

  return (await response.json()) as ParagraphCommentCountsResponse;
}

function toSuccessResult(key: string, data: ParagraphCommentCountsResponse) {
  return {
    key,
    counts: data.counts ?? {},
    total: data.total ?? 0,
    error: null,
  };
}

function toErrorResult(key: string, err: unknown) {
  return {
    key,
    counts: {},
    total: 0,
    error: err instanceof Error ? err.message : 'Unknown error',
  };
}

export function useParagraphCommentCounts(postId?: number, chapterId?: string) {
  const { data: session } = useSession();
  const accessToken = session?.accessToken;
  const queryKey = postId && chapterId ? `${postId}:${chapterId}` : null;

  const [result, setResult] = useState<{
    key: string | null;
    counts: Record<string, number>;
    total: number;
    error: string | null;
  }>({ key: null, counts: {}, total: 0, error: null });

  const fetchCounts = useCallback(async () => {
    if (!postId || !chapterId) {
      return;
    }

    const key = `${postId}:${chapterId}`;

    try {
      const data = await requestParagraphCommentCounts(postId, chapterId, accessToken);
      setResult(toSuccessResult(key, data));
    } catch (err) {
      setResult(toErrorResult(key, err));
    }
  }, [accessToken, chapterId, postId]);

  useEffect(() => {
    if (!postId || !chapterId) {
      return;
    }

    const key = `${postId}:${chapterId}`;
    const abortController = new AbortController();

    requestParagraphCommentCounts(postId, chapterId, accessToken, abortController.signal)
      .then((data) => {
        setResult(toSuccessResult(key, data));
      })
      .catch((err: unknown) => {
        if (abortController.signal.aborted) {
          return;
        }
        setResult(toErrorResult(key, err));
      });

    return () => abortController.abort();
  }, [accessToken, chapterId, postId]);

  const isCurrent = result.key === queryKey;

  return {
    counts: isCurrent ? result.counts : {},
    total: isCurrent ? result.total : 0,
    loading: Boolean(queryKey) && !isCurrent,
    error: isCurrent ? result.error : null,
    refetch: fetchCounts,
  };
}
